import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDashboard } from '../hooks/useDashboard';
import { Button } from '../components/ui';
import Spinner from '../components/ui/Spinner';
import StatsBar from '../components/dashboard/StatsBar';
import ActiveEntriesSection from '../components/dashboard/ActiveEntriesSection';
import WinsSection from '../components/dashboard/WinsSection';
import SellerSection from '../components/dashboard/SellerSection';

/**
 * Dashboard Page
 *
 * User's personal dashboard showing:
 * - Stats overview (entries, wins, earnings)
 * - Active entries
 * - Wins needing action
 * - Seller section (if applicable)
 */
export default function Dashboard() {
  const { user, userProfile, hasStripeConnect } = useAuth();
  const {
    entries,
    categorizedEntries,
    categorizedRaffles,
    pendingHandovers,
    stats,
    loading,
    error,
    isSeller,
  } = useDashboard();

  const [viewMode, setViewMode] = useState('buyer'); // 'buyer' | 'seller'

  // Build raffle lookup map for entries
  const raffleLookup = useMemo(() => {
    const lookup = {};
    // We need to fetch raffles for entries - this is a limitation
    // For now, entries contain some raffle data inline
    entries.forEach(e => {
      if (e.raffleId && !lookup[e.raffleId]) {
        lookup[e.raffleId] = {
          id: e.raffleId,
          title: e.raffleTitle || 'Loading...',
          photos: e.rafflePhotos || [],
          ticketsSold: e.raffleTotalSold || 0,
          ticketPrice: e.ticketPrice || 0,
          endsAt: e.raffleEndsAt,
        };
      }
    });
    return lookup;
  }, [entries]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="bg-midnight min-h-screen">
      {/* Header */}
      <div className="bg-midnight-light border-b border-cream/5">
        <div className="page-container py-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="text-center sm:text-left">
              <h1 className="font-display font-bold text-2xl md:text-3xl text-cream">
                Welcome back, {user?.displayName?.split(' ')[0] || 'there'}
              </h1>
              <p className="text-cream/60 mt-1">
                Here's what's happening with your competitions
              </p>
            </div>

            {/* View mode toggle (if seller) */}
            {isSeller && (
              <div className="flex items-center justify-center sm:justify-end gap-2 bg-midnight rounded-lg p-1 self-center sm:self-auto">
                <button
                  onClick={() => setViewMode('buyer')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'buyer'
                      ? 'bg-primary text-white'
                      : 'text-cream/60 hover:text-cream'
                  }`}
                >
                  Buying
                </button>
                <button
                  onClick={() => setViewMode('seller')}
                  className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                    viewMode === 'seller'
                      ? 'bg-primary text-white'
                      : 'text-cream/60 hover:text-cream'
                  }`}
                >
                  Selling
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="page-container py-8">
        {/* Stats Bar */}
        <StatsBar
          stats={stats}
          isSeller={viewMode === 'seller'}
          className="mb-8"
        />

        {/* Main Content */}
        <div className="grid lg:grid-cols-2 gap-6">
          {viewMode === 'buyer' ? (
            <>
              {/* Active Entries */}
              <ActiveEntriesSection
                entries={entries}
                raffles={raffleLookup}
              />

              {/* Wins Section */}
              <WinsSection
                entries={entries}
                raffles={raffleLookup}
                pendingHandovers={pendingHandovers}
              />
            </>
          ) : (
            <>
              {/* Seller Section */}
              <div className="lg:col-span-2">
                <SellerSection
                  raffles={categorizedRaffles.active.concat(
                    categorizedRaffles.completed,
                    categorizedRaffles.cancelled
                  )}
                  pendingHandovers={pendingHandovers}
                  hasStripeConnect={hasStripeConnect}
                />
              </div>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="mt-8 pt-8 border-t border-cream/5">
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/browse">
              <Button variant="outline" size="sm">
                <BrowseIcon className="w-4 h-4 mr-2 inline-block" />
                <span>Browse</span>
              </Button>
            </Link>
            <Link to="/my-entries">
              <Button variant="outline" size="sm">
                <TicketIcon className="w-4 h-4 mr-2 inline-block" />
                <span>Entries</span>
              </Button>
            </Link>
            {isSeller && (
              <Link to="/my-competitions">
                <Button variant="outline" size="sm">
                  <ListIcon className="w-4 h-4 mr-2 inline-block" />
                  <span>Competitions</span>
                </Button>
              </Link>
            )}
            <Link to="/create">
              <Button variant="accent" size="sm">
                <PlusIcon className="w-4 h-4 mr-2 inline-block" />
                <span>Create</span>
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icons
function BrowseIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  );
}

function TicketIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
    </svg>
  );
}

function ListIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v16m8-8H4" />
    </svg>
  );
}
